/**
 * @fileoverview Profit by Country - horizontal bar chart using ShadCN Charts (Recharts)
 * @module shared/components/ui/ProfitByCountry
 */

import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/utils/cn'
import { Card } from '@/shared/components/ui'
import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts'
import { ChartConfig, ChartContainer, ChartTooltip } from '@/components/ui/chart'
import { useNavigate } from 'react-router-dom'
import { ChartCardHeader } from './ChartCardHeader'
import { Globe } from 'lucide-react'
import { ChartEmptyState } from './ChartEmptyState'
import { ChartProfitTooltip, type ChartTooltipMetric } from './ChartProfitTooltip'
import { getThemeColors } from '@/components/ui/chart-utils'
import { useProfitByCountryData } from '@/shared/hooks/dashboard'
import { useCompactMoney } from '@/shared/hooks/useCompactMoney'
import { useLabelResolver } from '@/shared/hooks/useLabelResolver'
import { useThemeSignal } from '@/shared/hooks/useThemeSignal'

interface ProfitByCountryProps {
  className?: string
}

interface ChartDataItem {
  name: string
  flag: string
  countryCode: string
  profit: number
  revenue: number
  adSpend: number
  cogs: number
  units: number
}

const FLAG_SIZE = 32
const ROW_HEIGHT = 52

function codeToFlag(code: string): string {
  if (!code || code.length !== 2) return '🌐'
  const base = 127397
  const upper = code.toUpperCase()
  return String.fromCodePoint(...[...upper].map(c => base + c.charCodeAt(0)))
}

export function ProfitByCountry({ className }: ProfitByCountryProps): ReactElement {
  const { t, i18n } = useTranslation('features-home')
  const { data, isLoading, isError } = useProfitByCountryData()
  const compactMoney = useCompactMoney()
  const navigate = useNavigate()
  const { countryLabel } = useLabelResolver()
  useThemeSignal()

  const navigateToCountry = (countryCode: string): void => {
    navigate(`/dashboard/performance?columns=sales,totalExpenses,profit,margin&sort=profit:desc&view=countries&expandCountry=${encodeURIComponent(countryCode)}`)
  }

  const colors = getThemeColors()

  const chartData: ChartDataItem[] = useMemo(() => {
    const countries = data ?? []
    return countries
      .map(c => ({
        name: countryLabel(c.country),
        flag: codeToFlag(c.country),
        countryCode: c.country,
        profit: c.profit,
        revenue: c.revenue,
        adSpend: c.adSpend,
        cogs: c.cogs,
        units: c.units,
      }))
      .sort((a, b) => b.profit - a.profit)
      .slice(0, 6)
  }, [data, countryLabel])

  const chartConfig = useMemo(
    () =>
      ({
        profit: {
          label: t('charts.profitByCountry.profit', { defaultValue: 'Profit' }),
          color: colors.positive,
        },
      }) satisfies ChartConfig,
    [colors.positive, t]
  )

  // Negative bars extend left of zero; their value label is drawn to the left of
  // the bar and needs a left margin so it stays inside the (self-clipping) SVG.
  const hasNegativeProfit = chartData.some(item => item.profit < 0)

  if (isLoading) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.profitByCountry.title')}
          href="/dashboard/performance?columns=sales,totalExpenses,profit,margin&sort=profit:desc&view=countries"
        />
        <div className="px-6 pt-4 pb-8">
          <div className="flex h-chart-md items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-foreground/20 border-t-foreground" />
          </div>
        </div>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.profitByCountry.title')}
          href="/dashboard/performance?columns=sales,totalExpenses,profit,margin&sort=profit:desc&view=countries"
        />
        <div className="px-6 pt-4 pb-8">
          <div className="flex h-chart-md items-center justify-center text-[--color-negative]">
            {t('charts.profitByCountry.error')}
          </div>
        </div>
      </Card>
    )
  }

  if (!chartData.length) {
    return (
      <Card className={cn('rounded-xl border border-border/40 bg-card/30 overflow-hidden', className)}>
        <ChartCardHeader
          title={t('charts.profitByCountry.title')}
          href="/dashboard/performance?columns=sales,totalExpenses,profit,margin&sort=profit:desc&view=countries"
        />
        <div className="px-6 pt-4 pb-8">
          <ChartEmptyState
            variant="bars"
            icon={Globe}
            message={t('charts.profitByCountry.noData')}
            hint={t('charts.emptyHint')}
            className="h-chart-md"
          />
        </div>
      </Card>
    )
  }

  return (
    <Card className={cn('rounded-xl border border-border/40 bg-card/30', className)}>
      {/* Header section */}
      <ChartCardHeader
        title={t('charts.profitByCountry.title')}
        href="/dashboard/performance?columns=sales,totalExpenses,profit,margin&sort=profit:desc&view=countries"
        className="rounded-t-xl"
      />

      {/* Chart section with border separator */}
      <div className="px-6 pt-4 pb-8 flex" style={{ height: `${chartData.length * ROW_HEIGHT + 48}px` }}>
        {/* Country flags column - CSS Grid matches Recharts bar distribution */}
        <div
          className="grid pr-3"
          style={{ gridTemplateRows: `repeat(${chartData.length}, 1fr)` }}
        >
          {chartData.map(item => (
            <div
              key={item.countryCode}
              className="flex items-center justify-center cursor-pointer"
              onClick={() => navigateToCountry(item.countryCode)}
            >
              <span style={{ fontSize: `${FLAG_SIZE}px`, lineHeight: 1 }}>{item.flag}</span>
            </div>
          ))}
        </div>

        {/* Bar chart */}
        <ChartContainer config={chartConfig} className="flex-1">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{
              left: hasNegativeProfit ? 88 : 0,
              right: 80,
              top: 0,
              bottom: 0,
            }}
            className="cursor-pointer"
            onClick={(state: Record<string, unknown>) => {
              const idx = state?.activeTooltipIndex as number | undefined
              if (idx != null && chartData[idx]) navigateToCountry(chartData[idx].countryCode)
            }}
          >
            <defs>
              {/* Lengthwise fade (dim base, full color at the tip) — horizontal
                  counterpart of the donuts' top-lit slice gradients. */}
              <linearGradient id="profit-country-bar-fill" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="var(--color-profit)" stopOpacity={0.6} />
                <stop offset="100%" stopColor="var(--color-profit)" stopOpacity={0.95} />
              </linearGradient>
            </defs>
            <YAxis dataKey="name" type="category" hide />
            <XAxis
              dataKey="profit"
              type="number"
              hide
              domain={[(min: number) => Math.min(0, min), (max: number) => Math.max(0, max)]}
            />
            <ChartTooltip
              cursor={{ fill: 'hsl(var(--muted) / 0.2)' }}
              wrapperStyle={{ zIndex: 50 }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null
                const item = payload[0]?.payload as ChartDataItem
                if (!item) return null

                const roas = item.adSpend > 0 ? item.revenue / item.adSpend : null
                const metrics: ChartTooltipMetric[] = [
                  { label: t('charts.tooltipMetrics.revenue'), value: compactMoney(item.revenue) },
                  { label: t('charts.tooltipMetrics.adSpend'), value: compactMoney(item.adSpend) },
                  {
                    label: t('charts.tooltipMetrics.roas'),
                    value: roas != null ? `${roas.toFixed(1)}×` : '—',
                  },
                  { label: t('charts.tooltipMetrics.cogs'), value: compactMoney(item.cogs) },
                  {
                    label: t('charts.tooltipMetrics.unitsSold'),
                    value: Math.round(item.units).toLocaleString(i18n.language),
                  },
                ]

                return (
                  <ChartProfitTooltip
                    lead={<span style={{ fontSize: '1.125rem', lineHeight: 1 }}>{item.flag}</span>}
                    name={item.name}
                    profit={item.profit}
                    profitLabel={t('charts.profitByCountry.profit', { defaultValue: 'Profit' })}
                    formatMoney={compactMoney}
                    metrics={metrics}
                  />
                )
              }}
            />
            <Bar
              dataKey="profit"
              fill="url(#profit-country-bar-fill)"
              radius={9}
              barSize={18}
              background={{ fill: 'var(--color-muted)', fillOpacity: 0.25, radius: 9 }}
              activeBar={{ fill: 'var(--color-profit)' }}
            >
              <LabelList
                dataKey="profit"
                content={(props: {
                  x?: unknown
                  y?: unknown
                  width?: unknown
                  height?: unknown
                  value?: unknown
                }) => {
                  // Recharts gives x = zero baseline and a signed width, so the
                  // bar's left/right edges must be derived with min/max.
                  const x = Number(props.x) || 0
                  const y = Number(props.y) || 0
                  const width = Number(props.width) || 0
                  const height = Number(props.height) || 0
                  const barLeft = Math.min(x, x + width)
                  const barRight = Math.max(x, x + width)
                  const numeric = typeof props.value === 'number' ? props.value : 0
                  const positive = numeric >= 0
                  return (
                    <text
                      x={positive ? barRight + 8 : barLeft - 8}
                      y={y + height / 2}
                      textAnchor={positive ? 'start' : 'end'}
                      dominantBaseline="central"
                      className="fill-foreground font-mono font-medium"
                      fontSize={14}
                    >
                      {compactMoney(numeric)}
                    </text>
                  )
                }}
              />
            </Bar>
          </BarChart>
        </ChartContainer>
      </div>
    </Card>
  )
}
